import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import {
  SegmentTypes,
  useGetCycleSegments,
} from '~pages/LiveMap/Timeline/components/hooks/useGetCycleSegments';
import { useIndicatorValues } from '~pages/LiveMap/Timeline/components/hooks/useIndicatorValues';
import { useTimeNormalization } from '~pages/LiveMap/Timeline/components/hooks/useTimeNormalization';
import TimeIndicator from '~pages/LiveMap/Timeline/components/TimeIndicator';
import { useStores } from '~store';
import { formatSpeed } from '~utils/speed';

import {
  getNoSignalBlocks,
  TimelineTickLabels,
  TimelineTickLines,
  TimelineTimeDisplay,
} from './timelineTicks';

const SEGMENT_HEIGHT = 16;
const SEGMENT_MARGIN = 4;
const LABEL_WIDTH = 80;

// A single-ping segment has start === end, so its left and right offsets meet and
// the bar computes to zero width. Clamp to a small minimum so a sparse ping with
// no neighbours still renders as a visible tick rather than vanishing (TRE-15314).
const MIN_SEGMENT_WIDTH_IN_PX = 3;

const SEGMENT_STYLES: Record<string, { backgroundColor: string }> = {
  [SegmentTypes.PICK_UP]: { backgroundColor: '#106FEE' },
  [SegmentTypes.EN_ROUTE]: { backgroundColor: '#78B2FF' },
  [SegmentTypes.DROP_OFF]: { backgroundColor: '#133080' },
  [SegmentTypes.LOGGING]: { backgroundColor: 'black' },
};

function getSegmentRow(type: string, hasLogging: boolean): number {
  switch (type) {
    case SegmentTypes.PICK_UP:
      return 0;
    case SegmentTypes.EN_ROUTE:
      return 1;
    case SegmentTypes.DROP_OFF:
      return 2;
    case SegmentTypes.LOGGING:
      return hasLogging ? 3 : -1;
    default:
      return -1;
  }
}

function getRowCount(hasLogging: boolean): number {
  return hasLogging ? 4 : 3;
}

const ROW_LABELS: { key: string; i18nKey: string }[] = [
  { key: 'pickUpLabel', i18nKey: 'live_map.timeline.pickup' },
  { key: 'enRouteLabel', i18nKey: 'live_map.timeline.enroute' },
  { key: 'dropOffLabel', i18nKey: 'live_map.timeline.dropoff' },
];

interface Props {
  currentPing: LocationHistoryMinimal | null;
  pings: LocationHistoryMinimal[];
}

export const DistanceCycleTimeline = ({ currentPing, pings }: Props) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const isMetric = userStore?.userCompany.isMeters;

  const firstPingTime = pings[0].occurredAt;
  const lastPingTime = pings[pings.length - 1].occurredAt;
  const startMs = firstPingTime.valueOf();
  const endMs = lastPingTime.valueOf();
  const minTime = firstPingTime.toISOString();
  const maxTime = lastPingTime.toISOString();

  const normalize = useTimeNormalization(minTime, maxTime);

  const segments = useGetCycleSegments(pings);
  const noSignalBlocks = useMemo(
    () => getNoSignalBlocks(segments, firstPingTime, lastPingTime),
    [segments, firstPingTime, lastPingTime],
  );

  const hasLogging = segments.some((segment) => segment.type === SegmentTypes.LOGGING);

  const { graphRef, indicatorValues } = useIndicatorValues(
    minTime,
    maxTime,
    noSignalBlocks,
  );

  const rowCount = getRowCount(hasLogging);
  const graphHeight = rowCount * SEGMENT_HEIGHT + (rowCount + 1) * SEGMENT_MARGIN;

  const speedLabel = currentPing?.speed
    ? formatSpeed(parseFloat(currentPing.speed), !!isMetric)
    : null;

  return (
    <>
      <TimelineTimeDisplay
        indicatorTime={indicatorValues.indicatorTime}
        label={t('live_map.timeline.time')}
      >
        {speedLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('live_map.timeline.speed')}
            </Typography>
            <Typography variant="body2">{speedLabel}</Typography>
          </Box>
        )}
      </TimelineTimeDisplay>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${LABEL_WIDTH}px 1fr`,
          columnGap: 1,
        }}
      >
        {ROW_LABELS.map((row, rowIndex) => (
          <Typography
            key={row.key}
            variant="body2"
            sx={{
              gridColumn: 1,
              gridRow: rowIndex + 1,
              lineHeight: `${SEGMENT_HEIGHT}px`,
              mt: `${SEGMENT_MARGIN}px`,
              fontSize: '0.6875rem',
            }}
          >
            {t(row.i18nKey)}
          </Typography>
        ))}
        {hasLogging && (
          <Typography
            variant="body2"
            sx={{
              gridColumn: 1,
              gridRow: 4,
              lineHeight: `${SEGMENT_HEIGHT}px`,
              mt: `${SEGMENT_MARGIN}px`,
              fontSize: '0.6875rem',
            }}
          >
            {t('live_map.timeline.logging')}
          </Typography>
        )}

        <Box
          sx={{
            gridColumn: 2,
            gridRow: `1 / ${rowCount + 2}`,
          }}
        >
          <Box
            ref={graphRef}
            sx={{
              position: 'relative',
              height: graphHeight,
              border: `1px solid ${theme.brandV2.colors.treadGray6}`,
              borderRadius: theme.brandV2.borderRadius,
              backgroundColor: theme.semantic.surfaceRaised,
              overflow: 'hidden',
            }}
          >
            <TimeIndicator
              visible={indicatorValues.indicatorVisible}
              position={indicatorValues.indicatorPosition}
              time={indicatorValues.indicatorTime}
            />

            <TimelineTickLines startMs={startMs} endMs={endMs} normalize={normalize} />

            {/* No-signal gaps are a backdrop, so render them first. A single-ping
                cycle segment collapses to a thin tick that sits right at the edge of
                the following gap; drawing the gap on top would paint over it, hiding
                sparse pings the rest of this work set out to surface (TRE-15314). */}
            {noSignalBlocks.map(([start, end]) => (
              <Box
                key={`nosignal_${start.valueOf()}`}
                sx={{
                  position: 'absolute',
                  left: normalize(start.toISOString()),
                  right: normalize(end.toISOString(), true),
                  height: '100%',
                  bottom: 0,
                  backgroundColor: theme.brandV2.colors.treadGray10,
                }}
              />
            ))}

            {segments.map((segment, index) => {
              const style = SEGMENT_STYLES[segment.type];
              const row = getSegmentRow(segment.type, hasLogging);
              if (!style || row < 0) return null;

              const top = SEGMENT_MARGIN + row * (SEGMENT_HEIGHT + SEGMENT_MARGIN);

              return (
                <Box
                  key={`segment_${index}`}
                  sx={{
                    position: 'absolute',
                    left: normalize(segment.start.toISOString()),
                    right: normalize(segment.end.toISOString(), true),
                    minWidth: `${MIN_SEGMENT_WIDTH_IN_PX}px`,
                    height: SEGMENT_HEIGHT,
                    top: top,
                    backgroundColor: style.backgroundColor,
                  }}
                />
              );
            })}
          </Box>

          <TimelineTickLabels startMs={startMs} endMs={endMs} normalize={normalize} />
        </Box>
      </Box>
    </>
  );
};
